/**
 * CommandPalette — Cmd+K / Ctrl+K quick-nav overlay.
 *
 * Lightweight implementation without a third-party cmdk dep. Supports:
 *   - Fuzzy-ish substring match on label + section (cheap + predictable).
 *   - Keyboard nav (↑/↓/Enter/Esc).
 *   - Quick actions (e.g. "New post") in addition to routes.
 *   - Focus trap on open; returns focus to previously-active element on close.
 *
 * The palette is rendered by AppLayout, which owns the open-state so a
 * global Cmd+K listener works from any route. Keep it pure — no router
 * coupling beyond ``onNavigate`` callback.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Command as CommandIcon,
  CornerDownLeft,
  Search,
  Sparkles,
} from 'lucide-react';

export interface CommandItem {
  id: string;
  label: string;
  section?: string;
  path?: string;
  icon?: React.ElementType;
  /** Optional — runs instead of navigate(path) when the user picks this. */
  action?: () => void;
  /** Hints "AI" visual accent (emerald) instead of the default (blue). */
  aiAccent?: boolean;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
  onNavigate: (path: string) => void;
}

function score(query: string, item: CommandItem): number {
  if (!query) {
    return 1;
  }
  const q = query.toLowerCase();
  const label = item.label.toLowerCase();
  const section = (item.section || '').toLowerCase();
  if (label.startsWith(q)) {
    return 100;
  }
  if (label.includes(q)) {
    return 50;
  }
  if (section.includes(q)) {
    return 20;
  }
  // Cheap subseq score — letters of q must appear in label in order.
  let i = 0;
  for (const ch of label) {
    if (ch === q[i]) {
      i += 1;
      if (i === q.length) {
        return 10;
      }
    }
  }
  return 0;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onClose,
  items,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter + rank items by query.
  const ranked = useMemo(() => {
    return items
      .map((item) => ({ item, s: score(query, item) }))
      .filter(({ s }) => s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 50)
      .map(({ item }) => item);
  }, [items, query]);

  // Reset when opened; remember last focus so we can restore on close.
  useEffect(() => {
    if (open) {
      lastFocusedRef.current = document.activeElement as HTMLElement | null;
      setQuery('');
      setActive(0);
      // Next tick so the input exists in the DOM.
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      lastFocusedRef.current?.focus?.();
    }
  }, [open]);

  // Keep active in-range when the ranked list shrinks.
  useEffect(() => {
    if (active >= ranked.length) {
      setActive(Math.max(0, ranked.length - 1));
    }
  }, [active, ranked.length]);

  const run = useCallback(
    (item: CommandItem | undefined) => {
      if (!item) {
        return;
      }
      onClose();
      if (item.action) {
        item.action();
        return;
      }
      if (item.path) {
        onNavigate(item.path);
      }
    },
    [onClose, onNavigate],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, ranked.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      run(ranked[active]);
    }
  };

  // Scroll active item into view.
  useEffect(() => {
    if (!open) {
      return;
    }
    const el = listRef.current?.children?.[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="command-palette-title"
    >
      {/* Dedicated backdrop button — click closes, keyboard closes via
          the input's Escape handler. Splitting the backdrop off the
          dialog container keeps jsx-a11y happy (role="dialog" shouldn't
          also be a click target). */}
      <button
        type="button"
        onClick={onClose}
        aria-label={t('cmdk_close', 'Close command palette')}
        className="absolute inset-0 bg-n-text/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative z-10 w-full max-w-[640px] rounded-[20px] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(247,247,245,0.96))] ring-1 ring-n-border shadow-[0_32px_80px_-16px_rgba(0,0,0,0.24)] overflow-hidden">
        <h2 id="command-palette-title" className="sr-only">
          {t('cmdk_title', 'Command palette')}
        </h2>
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-n-border">
          <Search className="w-4 h-4 text-n-text-lt flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={t(
              'cmdk_placeholder',
              'Jump to page, run a command…',
            )}
            aria-label={t('cmdk_input_label', 'Search commands')}
            className="flex-1 bg-transparent text-[14px] text-n-text placeholder:text-n-text-lt outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-n-hover ring-1 ring-n-border text-[10.5px] font-medium text-n-text-md">
            <CommandIcon className="w-3 h-3" />K
          </kbd>
        </div>

        {ranked.length === 0 ? (
          <div className="px-4 py-10 text-center text-[13px] text-n-text-md">
            {t('cmdk_no_results', 'Nothing matches your search.')}
          </div>
        ) : (
          <ul
            ref={listRef}
            role="listbox"
            aria-label={t('cmdk_results_label', 'Search results')}
            className="max-h-[420px] overflow-y-auto py-2"
          >
            {ranked.map((item, idx) => {
              const Icon = item.icon || Sparkles;
              const isActive = idx === active;
              return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- keyboard nav is handled by the input above; mouse click is a convenience on top of that.
                <li
                  key={item.id}
                  role="option"
                  aria-selected={isActive}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => run(item)}
                  className={`mx-2 px-3 py-2 rounded-lg flex items-center gap-3 cursor-pointer ${
                    isActive
                      ? item.aiAccent
                        ? 'bg-emerald-50 ring-1 ring-emerald-200'
                        : 'bg-n-blue-bg ring-1 ring-n-blue/30'
                      : 'hover:bg-n-hover/70'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.aiAccent
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-n-hover text-n-text-md'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-[13.5px] font-medium truncate ${
                        isActive && item.aiAccent
                          ? 'text-emerald-800'
                          : isActive
                            ? 'text-n-blue'
                            : 'text-n-text'
                      }`}
                    >
                      {item.label}
                    </p>
                    {item.section && (
                      <p className="text-[11px] text-n-text-lt truncate">
                        {item.section}
                      </p>
                    )}
                  </div>
                  {isActive && (
                    <CornerDownLeft className="w-3.5 h-3.5 text-n-text-lt flex-shrink-0" />
                  )}
                  {!isActive && item.path && (
                    <ArrowRight className="w-3.5 h-3.5 text-n-text-lt/60 flex-shrink-0" />
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="px-4 py-2.5 border-t border-n-border bg-n-page/60 flex items-center justify-between gap-3 text-[11px] text-n-text-lt">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-white ring-1 ring-n-border text-[10px]">↑↓</kbd>{' '}
              {t('cmdk_hint_nav', 'navigate')}
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-white ring-1 ring-n-border text-[10px]">↵</kbd>{' '}
              {t('cmdk_hint_select', 'open')}
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-white ring-1 ring-n-border text-[10px]">Esc</kbd>{' '}
              {t('cmdk_hint_close', 'close')}
            </span>
          </div>
          <span className="text-n-text-lt/80">
            {ranked.length} {t('cmdk_results_count', 'results')}
          </span>
        </div>
      </div>
    </div>
  );
};
